/**
 * What a sheet asks of a student: minutes against the target, points, how the
 * difficulty is spread. Pure, so the strip over the item list and the Checks tab agree
 * and both can be tested without a browser.
 *
 * Every figure comes from the exercise — `timeMinutes`, `points`, camels — with the
 * sheet's own overrides on top. An exercise with no estimate is counted as unknown rather
 * than as zero, so a total never looks smaller than it is without saying so.
 */
import { parseExercise, totalPoints, type LibraryItem, type SheetItem } from '@studio/shared';
import { tx } from './i18n';

/** the parts of an exercise's library entry the budget reads */
export type BudgetMeta = Pick<LibraryItem, 'timeMinutes' | 'points' | 'difficultySource'>;

export type Camel = 1 | 2 | 3 | 4;

export interface Budget {
  /** exercises on the sheet; blocks and page breaks do not count */
  exercises: number;
  /** estimated minutes of the exercises that have an estimate */
  minutes: number;
  /** exercises without a time estimate */
  unknownMinutes: number;
  points: number;
  /** exercises with no points, neither on the sheet nor in the library */
  unknownPoints: number;
  /**
   * exercises per difficulty, camels 1–4 as the sheets print them; `none` were set to
   * no camels on purpose, `unknown` have none in the library and none set here
   */
  histogram: Record<Camel | 'none' | 'unknown', number>;
  /** the sheet's `targetMinutes`, when one is set */
  target?: number;
  /**
   * `none` without a target; `over` past it; `under` well short of it (below
   * UNDER_SHARE); `ok` in between.
   */
  status: 'none' | 'under' | 'ok' | 'over';
}

/**
 * Below this share of the target a sheet counts as short. A tutorial is planned with
 * slack for questions, so landing a little under is the normal case, not a warning.
 */
export const UNDER_SHARE = 0.7;

/**
 * Minutes an exercise takes: its estimate; null when it has none. An item is its exercise,
 * so a sheet has no time of its own for it (a different one is a variant of the exercise).
 */
export function itemMinutes(_item: unknown, meta: BudgetMeta | undefined): number | null {
  const m = meta?.timeMinutes;
  // exercises written in Studio without an estimate arrive as 0
  return typeof m === 'number' && Number.isFinite(m) && m > 0 ? m : null;
}

/**
 * Points the exercise is worth: for a Typst body with subtasks, their sum in the text the
 * sheet prints, else the exercise's own. The sum is what the sheet prints.
 */
export function itemPoints(item: Pick<SheetItem, 'body'>, meta: BudgetMeta | undefined): number | null {
  if (item.body?.includes('#subtask(')) {
    const sum = totalPoints(parseExercise(item.body));
    if (sum !== null) return sum;
  }
  const p = meta?.points;
  return typeof p === 'number' && Number.isFinite(p) && p >= 0 ? p : null;
}

/**
 * Camels 1–4 for an exercise: the camels it prints. The corpus annotates up to 5 in a few
 * places; those count as 4, the hardest the sheets draw. Nothing, or 0, is unknown.
 */
export function itemCamels(_item: unknown, meta: BudgetMeta | undefined): Camel | null {
  const n = meta?.difficultySource;
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 1) return null;
  return Math.min(4, Math.round(n)) as Camel;
}

export function computeBudget(
  doc: { items: SheetItem[]; meta: { targetMinutes?: number } },
  catalog: Record<string, BudgetMeta | undefined>,
): Budget {
  const histogram: Budget['histogram'] = { 1: 0, 2: 0, 3: 0, 4: 0, none: 0, unknown: 0 };
  let exercises = 0;
  let minutes = 0;
  let unknownMinutes = 0;
  let points = 0;
  let unknownPoints = 0;
  for (const item of doc.items) {
    if (item.kind !== 'exercise') continue;
    exercises++;
    const meta = item.exerciseId ? catalog[item.exerciseId] : undefined;
    const m = itemMinutes(item, meta);
    if (m === null) unknownMinutes++;
    else minutes += m;
    const p = itemPoints(item, meta);
    if (p === null) unknownPoints++;
    else points += p;
    histogram[itemCamels(item, meta) ?? 'unknown']++;
  }
  const raw = doc.meta.targetMinutes;
  const target = typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : undefined;
  const status: Budget['status'] =
    target === undefined ? 'none' : minutes > target ? 'over' : minutes < target * UNDER_SHARE ? 'under' : 'ok';
  return { exercises, minutes, unknownMinutes, points, unknownPoints, histogram, target, status };
}

/** "75 min", "1 h 30 min" — how long a sheet takes, the way a tutor would say it. */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) return tx('{minutes} min', { minutes });
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? tx('{h} h {m} min', { h, m }) : `${h} h`;
}
